import React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router";
import { IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AccountTypeEnum } from "@rentablo/api/apiv1";
import PlusIcon from "@icons/myicons/plus.svg?react";
import { ExtendedAccount } from "@features/depots/types";
import { useAccountsSortAndFilter } from "./useAccountsSortAndFilter";
import { AccountRowLoading } from "./AccountRow";
import { AccountsTableHeader } from "./AccountsTableHeader";
import { AccountView } from "./AccountView";
import { AccountPerformance } from "./AccountView/hooks/useAccountsPerformance";
import { getRedirectType } from "./utils";

const Container = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
`;

const NoDataContainer = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.palette.border.secondary};
  text-align: center;
  grid-column: 1 / -1;
`;

const RowsGrid = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
`;

const TitleRow = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  gap: theme.spacing(3),
  gridColumn: "1 / -1",
}));

interface Props {
  type: AccountTypeEnum;
  title: string;
  accounts?: ExtendedAccount[];
  refAccountsByDepotId?: Partial<Record<number, ExtendedAccount[]>>;
  isFetching: boolean;
  filter?: string;
  isLastGroup: boolean;
  onAdd: () => void;
  performanceById: Record<number, AccountPerformance>;
  performanceValues: Record<number, number>;
}

export const AccountsContainer = ({
  type,
  title,
  isFetching,
  accounts = [],
  refAccountsByDepotId = {},
  filter = "",
  onAdd,
  isLastGroup,
  performanceById,
  performanceValues,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { sortedAccounts, sortField, sortDirection, sort } =
    useAccountsSortAndFilter(accounts, { filter, performanceValues });

  return (
    <Container data-testid={`accounts-list-${type}`}>
      {isFetching && (
        <>
          <AccountRowLoading />
          <AccountRowLoading />
          <AccountRowLoading />
        </>
      )}
      {!isFetching && (
        <>
          <TitleRow>
            <Typography variant="h6">{title}</Typography>
            <IconButton
              sx={{
                width: "36px",
                height: "36px",
                borderRadius: "6px",
                backgroundColor: theme.palette.button.tertiaryButton.default,
              }}
              onClick={onAdd}
            >
              <PlusIcon />
            </IconButton>
          </TitleRow>
          <AccountsTableHeader
            type={type}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={sort}
          />
          {sortedAccounts.length > 0 ? (
            <RowsGrid>
              {sortedAccounts.map((account, index) => {
                const referenceAccounts = [
                  ...(refAccountsByDepotId[account.id] ?? []),
                ].sort((a, b) => {
                  if (a.id === account.contraAccountId) return -1;
                  if (b.id === account.contraAccountId) return 1;
                  return 0;
                });
                const isLastAccountInGroup =
                  index === sortedAccounts.length - 1;
                const redirectType = getRedirectType(account);
                return (
                  <React.Fragment key={`account-view-${account.id}`}>
                    <AccountView
                      isLast={
                        referenceAccounts.length === 0
                          ? isLastAccountInGroup && !isLastGroup
                          : false
                      }
                      account={account}
                      onClick={() => {
                        navigate(`/${redirectType}/${account.id}`);
                      }}
                      performance={performanceById[account.id]}
                    />
                    {referenceAccounts.map((referenceAccount, refIndex) => (
                      <AccountView
                        key={`reference-${referenceAccount.id}`}
                        isLast={
                          refIndex === referenceAccounts.length - 1 &&
                          isLastAccountInGroup &&
                          !isLastGroup
                        }
                        account={referenceAccount}
                        isPrimaryReference={
                          referenceAccounts.length > 1 &&
                          account.contraAccountId === referenceAccount.id
                        }
                        onClick={() => {
                          navigate(`/accounts/${referenceAccount.id}`);
                        }}
                        performance={performanceById[referenceAccount.id]}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </RowsGrid>
          ) : (
            <NoDataContainer>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                fontWeight={600}
                p={theme.spacing(6, 0)}
              >
                {t("depotsAccountsPage.noResults")}
              </Typography>
            </NoDataContainer>
          )}
        </>
      )}
    </Container>
  );
};
